import React from 'react';
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  XIcon,
  LinkedinIcon,
  EmailIcon,
  BlueskyIcon,
  BlueskyShareButton,
} from 'react-share';
import ButtonArticleShare from '../button_article_share/button_article_share';
import { trackActivity } from '../../services/activity/activity.api.service';
import styles from './share_content_toolbar.module.scss';
import { createQueryString } from '../../utilities/utilities';

const ICON_SIZE = 40;
const TARGET_TYPE = 'Article';
const ACTIVITY = 'share';

interface Props {
  contentId: number;
  contentUrl: string;
  contentTitle: string;
  contentTeaser: string;
  showSharingArticle: boolean;
  isArticlePaywalled: boolean;
}

const ShareContentToolbar = (props: Props): JSX.Element => {
  const {
    contentId,
    contentUrl,
    contentTitle,
    contentTeaser,
    showSharingArticle,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isArticlePaywalled,
  } = props;

  const onPrintIconClick = (): void => {
    trackActivity({
      activity: ACTIVITY,
      target_id: contentId.toString(),
      target_type: TARGET_TYPE,
      detail: 'print',
    });

    window.print();
  };

  const onShareWindowClose = (sharingService: string): void => {
    trackActivity({
      activity: ACTIVITY,
      target_id: contentId.toString(),
      target_type: TARGET_TYPE,
      detail: sharingService,
    });
  };

  const renderIcons: JSX.Element = (
    <>
      {showSharingArticle && <ButtonArticleShare articleId={contentId} />}
      <span className='mx-lg-0 mt-lg-25 mb-lg-0 mx-15 mt-15 mb-0'>
        <FacebookShareButton
          htmlTitle='Share on Facebook'
          url={
            contentUrl +
            createQueryString({
              utm_source: 'facebook',
              utm_medium: 'social',
              utm_campaign: 'devex_social_icons',
            })
          }
          hashtag='#Devex'
          onShareWindowClose={() => {
            onShareWindowClose('facebook');
          }}
        >
          <FacebookIcon size={ICON_SIZE} round />
        </FacebookShareButton>
      </span>
      <span className='mx-lg-0 mt-lg-25 mb-lg-0 mx-15 mt-15 mb-0'>
        <TwitterShareButton
          htmlTitle='Share on X'
          url={
            contentUrl +
            createQueryString({
              utm_source: 'twitter',
              utm_medium: 'social',
              utm_campaign: 'devex_social_icons',
            })
          }
          onShareWindowClose={() => {
            onShareWindowClose('twitter');
          }}
          title={contentTitle}
          hashtags={['Devex']}
          via='Devex'
        >
          <XIcon size={ICON_SIZE} round />
        </TwitterShareButton>
      </span>
      <span className='mx-lg-0 mt-lg-25 mb-lg-0 mx-15 mt-15 mb-0'>
        <LinkedinShareButton
          htmlTitle='Share on LinkedIn'
          url={
            contentUrl +
            createQueryString({
              utm_source: 'linkedin',
              utm_medium: 'social',
              utm_campaign: 'devex_social_icons',
            })
          }
          onShareWindowClose={() => {
            onShareWindowClose('linkedin');
          }}
          source='Devex'
          summary={contentTeaser}
          title={contentTitle}
        >
          <LinkedinIcon size={ICON_SIZE} round />
        </LinkedinShareButton>
      </span>
      <span className='mx-lg-0 mt-lg-25 mb-lg-0 mx-15 mt-15 mb-0'>
        <BlueskyShareButton
          htmlTitle='Share on Bluesky'
          url={
            contentUrl +
            createQueryString({
              utm_source: 'bluesky',
              utm_medium: 'social',
              utm_campaign: 'devex_social_icons',
            })
          }
          onShareWindowClose={() => {
            onShareWindowClose('bluesky');
          }}
          title={contentTitle}
        >
          <BlueskyIcon size={ICON_SIZE} round />
        </BlueskyShareButton>
      </span>
      {!showSharingArticle && (
        <span className='mx-lg-0 mt-lg-25 mb-lg-0 mx-15 mt-15 mb-0'>
          <EmailShareButton
            htmlTitle='Share via email'
            url={
              contentUrl +
              createQueryString({
                utm_source: 'devex',
                utm_medium: 'email',
                utm_campaign: 'devex_social_icons',
              })
            }
            // Disable onClick handler to open email window in a new tab
            // See https://github.com/nygardk/react-share/issues/153
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onClick={() => {}}
            subject={`Devex: ${contentTitle}`}
            body={`I thought you might be interested in reading:\n\n${contentTitle}\n\n`}
            onShareWindowClose={() => {
              onShareWindowClose('email');
            }}
            openShareDialogOnClick={true}
          >
            <EmailIcon size={ICON_SIZE} round />
          </EmailShareButton>
        </span>
      )}
      {!isArticlePaywalled && (
        <span
          data-testid='print-icon'
          onClick={onPrintIconClick}
          className='mx-lg-0 mt-lg-25 mb-lg-0 mx-15 mt-15 mb-0'
        >
          <i
            className={`
              ${styles.printIcon}
            fal icon-print
            `}
          />
        </span>
      )}
      {/* TODO: If !showSharingArticle, Email sharing button functionality */}
    </>
  );
  const renderDesktopToolbar: JSX.Element = (
    <div className={`${styles.desktop} d-none d-lg-block d-print-none mt-15`}>
      <div className={styles.desktopToolbar} data-testid='desktop-share-content-toolbar'>
        {renderIcons}
      </div>
    </div>
  );

  const renderMobileToolbar: JSX.Element = (
    <div
      className={`
        align-items-center sticky-top d-inline-flex d-lg-none mt-15 d-print-none text-center
        ${styles.mobile}
      `}
      data-testid='mobile-share-content-toolbar'
    >
      {renderIcons}
    </div>
  );

  return (
    <>
      {renderDesktopToolbar}
      {renderMobileToolbar}
    </>
  );
};

export default ShareContentToolbar;
